import { formatTimeOfDay } from "../lib/format"
import { plural } from "../lib/plural"

/**
 * Разница поясов филиала и устройства — словами.
 *
 * ПОЧЕМУ ЭТО ОТДЕЛЬНАЯ ПОЛОСА, А НЕ ПОДПИСЬ МЕЛКИМ. ТЗ §4.4: при смене филиала с другим
 * поясом интерфейс обязан сказать это ЯВНО, а не переставить цифры молча. Самая дорогая
 * авария продукта — владелец из Москвы, который прочитал «16:40» как своё время и позвонил
 * клиенту в Екатеринбург за два часа до визита. Полоса стоит под шапкой и висит всё время,
 * пока открыт филиал с чужим поясом; она НЕ входит в три служебные полосы и считается
 * отдельно, потому что говорит не о сбое, а о норме.
 */

export interface TimezoneNote {
  /** «Филиал «Екатеринбург, Малышева», время местное, +2 часа к вашему» — главное, жирным. */
  readonly lead: string
  /** «16:40 по времени салона · у вас сейчас 14:40» — обе цифры рядом, чтобы не считать. */
  readonly tail: string
}

/**
 * Города часовых поясов по-русски.
 *
 * ПОЧЕМУ СПИСОК, А НЕ Intl. Intl умеет назвать пояс («Москва, стандартное время»), но
 * формулировка отличается от движка к движку и от версии к версии, а в кабинете это
 * не украшение: подпись «время салона (Москва)» стоит рядом с цифрой, по которой человек
 * звонит клиенту. Список короткий, покрывает зоны, в которых работают салоны, и там,
 * где зона незнакомая, подпись честно обходится без города, а не выдумывает его.
 */
const ZONE_CITY: Readonly<Record<string, string>> = {
  "Europe/Kaliningrad": "Калининград",
  "Europe/Moscow": "Москва",
  "Europe/Simferopol": "Симферополь",
  "Europe/Kirov": "Киров",
  "Europe/Volgograd": "Волгоград",
  "Europe/Astrakhan": "Астрахань",
  "Europe/Saratov": "Саратов",
  "Europe/Ulyanovsk": "Ульяновск",
  "Europe/Samara": "Самара",
  "Asia/Yekaterinburg": "Екатеринбург",
  "Asia/Omsk": "Омск",
  "Asia/Novosibirsk": "Новосибирск",
  "Asia/Barnaul": "Барнаул",
  "Asia/Tomsk": "Томск",
  "Asia/Novokuznetsk": "Новокузнецк",
  "Asia/Krasnoyarsk": "Красноярск",
  "Asia/Irkutsk": "Иркутск",
  "Asia/Chita": "Чита",
  "Asia/Yakutsk": "Якутск",
  "Asia/Khandyga": "Хандыга",
  "Asia/Vladivostok": "Владивосток",
  "Asia/Ust-Nera": "Усть-Нера",
  "Asia/Magadan": "Магадан",
  "Asia/Sakhalin": "Южно-Сахалинск",
  "Asia/Srednekolymsk": "Среднеколымск",
  "Asia/Kamchatka": "Петропавловск-Камчатский",
  "Asia/Anadyr": "Анадырь",
  "Europe/Minsk": "Минск",
  "Asia/Almaty": "Алматы",
  "Asia/Aqtobe": "Актобе",
  "Asia/Tashkent": "Ташкент",
  "Asia/Bishkek": "Бишкек",
  "Asia/Tbilisi": "Тбилиси",
  "Asia/Yerevan": "Ереван",
  "Asia/Baku": "Баку",
  "Europe/Kyiv": "Киев",
}

export function zoneCity(timeZone: string): string | null {
  return ZONE_CITY[timeZone] ?? null
}

const PARTS = new Map<string, Intl.DateTimeFormat>()

function partsFormat(timeZone: string): Intl.DateTimeFormat {
  const existing = PARTS.get(timeZone)
  if (existing !== undefined) return existing
  const created = new Intl.DateTimeFormat("en-US", {
    timeZone,
    // hourCycle h23, а не hour12: false: при hour12:false часть движков печатает полночь
    // как «24», и сдвиг пояса уезжает ровно на сутки.
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  })
  PARTS.set(timeZone, created)
  return created
}

function readPart(parts: readonly Intl.DateTimeFormatPart[], type: string): number {
  const found = parts.find((part) => part.type === type)
  return found === undefined ? 0 : Number.parseInt(found.value, 10)
}

/**
 * Сдвиг пояса от UTC в минутах на заданный момент.
 *
 * Считается через форматирование, а не таблицей сдвигов: летнее время, исторические
 * переносы и решения парламентов живут в базе IANA внутри движка, и повторять их
 * в приложении — способ разойтись с ней на одну ночь в году.
 */
export function zoneOffsetMinutes(timeZone: string, at: Date): number {
  const parts = partsFormat(timeZone).formatToParts(at)
  const asUtc = Date.UTC(
    readPart(parts, "year"),
    readPart(parts, "month") - 1,
    readPart(parts, "day"),
    readPart(parts, "hour"),
    readPart(parts, "minute"),
  )
  // Секунды в снимке не участвуют, поэтому исходный момент округляется туда же.
  const base = Math.floor(at.getTime() / 60000) * 60000
  return Math.round((asUtc - base) / 60000)
}

/**
 * «+2 часа», «−3 часа», «+5 часов 45 минут».
 *
 * Минуты в подписи нужны не для красоты: Катманду и Калькутта сдвинуты на нецелое число
 * часов, и «+5 часов» там — прямая ложь о времени визита.
 */
export function formatZoneDifference(diffMinutes: number): string {
  const sign = diffMinutes < 0 ? "−" : "+"
  const total = Math.abs(diffMinutes)
  const hours = Math.floor(total / 60)
  const minutes = total % 60
  const parts: string[] = []
  if (hours > 0) parts.push(`${hours} ${plural(hours, "час", "часа", "часов")}`)
  if (minutes > 0) parts.push(`${minutes} ${plural(minutes, "минута", "минуты", "минут")}`)
  return `${sign}${parts.join(" ")}`
}

export interface TimezoneNoteInput {
  readonly branchName: string
  readonly branchTimeZone: string
  /** Пояс устройства. Берётся из браузера и служит ТОЛЬКО для этой подписи. */
  readonly viewerTimeZone: string
  readonly now: Date
}

/**
 * Полоса пояса или её отсутствие.
 *
 * null возвращается, когда пояса совпадают: сообщать «разницы нет» значит занять 30
 * пикселей рабочей области сообщением ни о чём. Сравниваются именно СДВИГИ, а не строки
 * зон: Europe/Moscow и Europe/Kirov — разные зоны с одинаковым временем, и полоса между
 * ними была бы ложной тревогой.
 */
export function buildTimezoneNote(input: TimezoneNoteInput): TimezoneNote | null {
  const diff =
    zoneOffsetMinutes(input.branchTimeZone, input.now) -
    zoneOffsetMinutes(input.viewerTimeZone, input.now)
  if (diff === 0) return null

  return {
    lead: `Филиал «${input.branchName}», время местное, ${formatZoneDifference(diff)} к вашему`,
    tail: `${formatTimeOfDay(input.now, input.branchTimeZone)} по времени салона · у вас сейчас ${formatTimeOfDay(input.now, input.viewerTimeZone)}`,
  }
}

/**
 * «Данные на 13:47 · время салона (Москва)» — подпись свежести в шапке.
 *
 * Города может не быть в списке зон; тогда подпись честно короче, но не врёт.
 * Момента данных может не быть вовсе — в первые секунды после входа; тогда о свежести
 * не говорится ничего, потому что говорить нечего.
 */
export function formatDataFreshness(dataAt: string | null, timeZone: string): string {
  const city = zoneCity(timeZone)
  const salon = city === null ? "время салона" : `время салона (${city})`
  if (dataAt === null) return salon
  return `Данные на ${formatTimeOfDay(dataAt, timeZone)} · ${salon}`
}

/**
 * Короткая подпись пояса для списка филиалов: «ваш пояс», «+2 ч», «−3 ч», «+5:45 ч».
 *
 * Она стоит справа от каждой строки и отвечает на единственный вопрос, который человек
 * задаёт перед переключением: «там сейчас другое время?» Совпадающий пояс подписывается
 * СЛОВАМИ, а не «+0 ч»: ноль читается как «не посчитали».
 */
export function formatShortZoneDiff(
  branchTimeZone: string,
  viewerTimeZone: string,
  now: Date,
): string {
  const diff = zoneOffsetMinutes(branchTimeZone, now) - zoneOffsetMinutes(viewerTimeZone, now)
  if (diff === 0) return "ваш пояс"

  const sign = diff < 0 ? "−" : "+"
  const total = Math.abs(diff)
  const hours = Math.floor(total / 60)
  const minutes = total % 60
  if (minutes === 0) return `${sign}${hours} ч`
  return `${sign}${hours}:${String(minutes).padStart(2, "0")} ч`
}

/**
 * Подпись пояса для человека: «Москва (UTC+3)», «Новосибирск (UTC+7)».
 *
 * ЗАЧЕМ. Машинное имя пояса (`Asia/Novosibirsk`) — это идентификатор базы IANA, а не
 * ответ на вопрос «какое время увидит мой клиент». На 4-А1 салон подтверждает пояс,
 * в котором будет работать вся запись: показывать ему строку из кода значит требовать
 * подтвердить то, что он не читает. Ошибка здесь стоит суток расхождения во всём журнале.
 *
 * Сдвиг считается на переданный момент, а не берётся таблицей: летом и зимой он разный,
 * и «UTC+3» у пояса с переходом верно ровно полгода.
 *
 * Города, которого нет в таблице, не выдумываем — остаётся машинное имя, но уже со
 * сдвигом: `Africa/Cairo (UTC+3)` понятнее, чем `Africa/Cairo`.
 */
export function zoneLabel(timeZone: string, at: Date): string {
  const offset = zoneOffsetMinutes(timeZone, at)
  const sign = offset < 0 ? "−" : "+"
  const total = Math.abs(offset)
  const hours = Math.floor(total / 60)
  const minutes = total % 60
  const tail = minutes === 0 ? "" : `:${String(minutes).padStart(2, "0")}`
  const utc = `UTC${sign}${String(hours)}${tail}`
  return `${zoneCity(timeZone) ?? timeZone} (${utc})`
}
